// WHIT-483 round 3: the rules of package bookings in "My bookings" — what may
// be paid on each, and reading the amount the customer types — kept apart
// from React so `node --test` can run them. taurus-api decides every limit
// (booking.laterPayment, amountDue) and checks it again when the payment
// order is made; this only says what to offer. Plain CommonJS.

// What the customer can do about paying a booking.
//   { kind: 'due', amount, payBy }        an exact amount is needed now: a
//                                         held booking, an accepted request
//   { kind: 'free', minimum, maximum, dueDate }
//                                         any amount in between, any number
//                                         of times until the final due date
//   { kind: 'closed', remaining, dueDate } the final due date has passed
//                                         with money still owed: no button,
//                                         the customer contacts us
//   { kind: 'none' }                      nothing to pay
const payOptions = booking => {
  if (!booking || !booking.bookingId) return { kind: 'none' };
  if (
    booking.nextAction === 'PAYMENT_REQUIRED' &&
    booking.amountDue &&
    booking.amountDue.amount > 0 &&
    ['OPTION', 'CONFIRMED'].includes(booking.status)
  ) {
    return { kind: 'due', amount: booking.amountDue, payBy: booking.payBy };
  }
  const later = booking.laterPayment;
  if (later && later.minimum && later.maximum) {
    return later.open
      ? {
          kind: 'free',
          minimum: later.minimum,
          maximum: later.maximum,
          dueDate: later.dueDate,
        }
      : { kind: 'closed', remaining: later.maximum, dueDate: later.dueDate };
  }
  return { kind: 'none' };
};

// The amount typed into the Pay field, in minor units (every package
// currency has two decimals). "477", "477.5", "477,50", "1 477" and
// "1,477.50" are read; anything else is null.
const parseAmount = text => {
  const value = String(text == null ? '' : text)
    .replace(/[\s  ]/g, '')
    .trim();
  if (!value) return null;
  let normal;
  if (/^\d{1,3}(,\d{3})+(\.\d{1,2})?$/.test(value)) {
    normal = value.replace(/,/g, '');
  } else if (/^\d{1,3}(\.\d{3})+(,\d{1,2})?$/.test(value)) {
    normal = value.replace(/\./g, '').replace(',', '.');
  } else if (/^\d+([.,]\d{1,2})?$/.test(value)) {
    normal = value.replace(',', '.');
  } else {
    return null;
  }
  const [whole, fraction = ''] = normal.split('.');
  return Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
};

// Why an amount cannot be paid, or null when it can.
const amountProblem = (amount, minimum, maximum) => {
  if (amount === null || !Number.isInteger(amount) || amount <= 0) {
    return 'invalid';
  }
  if (minimum && amount < minimum.amount) return 'tooLow';
  if (maximum && amount > maximum.amount) return 'tooHigh';
  return null;
};

// The amount to put in the field first: the whole remaining amount.
const amountInput = money =>
  money && typeof money.amount === 'number'
    ? money.amount % 100 === 0
      ? String(money.amount / 100)
      : (money.amount / 100).toFixed(2)
    : '';

// What "record the payment" answered, for the Pay panel.
//   { next: 'done', booking }   recorded (or the booking is being reconciled)
//   { next: 'wait' }            the provider has not finished: ask again
//   { next: 'failed', code }    nothing was recorded
const recordOutcome = result => {
  if (result && result.booking && !result.error) {
    return { next: 'done', booking: result.booking };
  }
  const code = result && result.error ? result.error.code : null;
  if (code === 'PAYMENT_PENDING' || code === 'PAYMENT_UNAVAILABLE') {
    return { next: 'wait' };
  }
  return { next: 'failed', code: code || null };
};

module.exports = {
  payOptions,
  parseAmount,
  amountProblem,
  amountInput,
  recordOutcome,
};
